import { Key, useMemo } from 'react';
import { useControlledState } from '@react-stately/utils';

export interface RowExpansionProps {
  /**
   * Defines row expansion behavior as below:
   *
   * - single: Maintain a single expanded row at a time, by collapsing other rows when a row is
   * expanded
   * - multiple: allow for multiple rows to be expanded at the same time
   */
  expansionMode?: 'single' | 'multiple' | 'none';
  /**
   * Ids of expanded rows. Used to control the state of expanded rows.
   **/
  expandedKeys?: Iterable<Key>;
  /**
   * Ids of the rows that should be expanded by default. The state of expanded rows will
   * be uncontrolled and local to InteractiveTable.
   **/
  defaultExpandedKeys?: Iterable<Key>;
  onExpandedKeysChange?: (keys: Set<Key>) => void;
}
/**
 */
export function useExpandableRowsState({
  expansionMode,
  expandedKeys: expandedKeysProp,
  defaultExpandedKeys,
  onExpandedKeysChange,
}: RowExpansionProps): {
  expandedKeys: Set<Key>;
  hasToggleExpansionColumn: boolean;
  toggleRowExpansion: (key: Key) => void;
} {
  const [expandedKeys, setExpandedKeys] = useControlledState(
    useMemo(
      () => (expandedKeysProp ? new Set(expandedKeysProp) : undefined),
      [expandedKeysProp]
    ),
    useMemo(
      () => (defaultExpandedKeys ? new Set(defaultExpandedKeys) : new Set()),
      [defaultExpandedKeys]
    ),
    onExpandedKeysChange
  );

  return {
    expandedKeys,
    hasToggleExpansionColumn: expansionMode !== 'none',
    toggleRowExpansion: (key: Key) => {
      const newExpandedKeys = new Set(
        expansionMode === 'single' ? [] : expandedKeys
      );
      if (expandedKeys.has(key)) {
        newExpandedKeys.delete(key);
      } else {
        newExpandedKeys.add(key);
      }
      setExpandedKeys(newExpandedKeys);
    },
  };
}
